/**
 * @fileoverview Better Auth client hooks. Template sync retired; safe to edit.
 */

/**
 * Better Auth React Client
 * 
 * Client-side hooks and utilities for Better Auth
 * Replaces next-auth/react
 */

"use client";

import { createAuthClient } from "better-auth/react";

// Get base URL for client
// Client-side: use window.location.origin
// Server-side (SSR): use relative URLs (empty string = same origin)
function getBaseUrl(): string {
  if (typeof window !== "undefined") {
    // Client-side: use current origin
    return window.location.origin;
  }
  
  // Server-side (SSR): Use relative URLs (empty string = same origin)
  // Better Auth will make requests relative to the current request origin
  // This avoids requiring Node.js scripts in client components
  return '';
}

/**
 * Better Auth client instance for React components
 * 
 * Usage:
 * ```tsx
 * import { useSession, signIn, signOut } from '@/lib/better-auth/client';
 * 
 * const { data: session, isPending } = useSession();
 * await signIn.email({ email, password });
 * await signOut();
 * ```
 * 
 * Note: baseURL is determined at runtime:
 * - Client-side: uses window.location.origin
 * - Server-side (SSR): uses NEXT_PUBLIC_APP_URL or relative URLs
 */
export const authClient = createAuthClient({
  baseURL: getBaseUrl(),
  // Add fetch configuration to handle errors gracefully
  fetchOptions: {
    credentials: 'include',
    onError: (error) => {
      const debugAuth = typeof process !== 'undefined' && process.env?.DEBUG_AUTH === 'true';
      if (debugAuth) {
        const errorInfo: Record<string, unknown> = {
          type: error?.constructor?.name || typeof error,
        };
        if (error instanceof Error) {
          errorInfo.message = error.message;
          errorInfo.stack = error.stack;
        }
        if (error && typeof error === 'object') {
          const errorObj = error as Record<string, unknown>;
          if (errorObj.response) {
            const response = errorObj.response as Response | Record<string, unknown>;
            errorInfo.response = {
              status: (response as Response).status ?? (response as Record<string, unknown>).status,
              statusText: (response as Response).statusText ?? (response as Record<string, unknown>).statusText,
              url: (response as Response).url ?? (response as Record<string, unknown>).url,
            };
          }
          if (errorObj.responseText) {
            try {
              const responseText = String(errorObj.responseText);
              errorInfo.responseText = responseText.length > 500 ? responseText.substring(0, 500) + '...' : responseText;
            } catch {
              errorInfo.responseText = '[Unable to serialize]';
            }
          }
          if (errorObj.request) {
            const request = errorObj.request as Request | Record<string, unknown>;
            errorInfo.request = {
              method: (request as Request).method ?? (request as Record<string, unknown>).method,
              url: (request as Request).url ?? (request as Record<string, unknown>).url,
            };
          }
          if (errorObj.error) {
            const nestedError = errorObj.error;
            if (nestedError && typeof nestedError === 'object' && 'message' in nestedError && 'name' in nestedError) {
              errorInfo.error = {
                message: (nestedError as { message?: unknown }).message,
                name: (nestedError as { name?: unknown }).name,
              };
            } else if (typeof nestedError === 'object') {
              errorInfo.error = nestedError;
            } else {
              errorInfo.error = String(nestedError);
            }
          }
          try {
            Object.keys(errorObj).forEach(key => {
              if (['response', 'responseText', 'request', 'error'].includes(key)) return;
              try {
                const value = errorObj[key];
                if (value === null ||
                    typeof value === 'string' ||
                    typeof value === 'number' ||
                    typeof value === 'boolean' ||
                    (typeof value === 'object' && !(value instanceof Response) && !(value instanceof Request))) {
                  errorInfo[key] = value;
                }
              } catch {
                // skip
              }
            });
          } catch {
            // skip
          }
        }
        if (Object.keys(errorInfo).length <= 1) {
          errorInfo.raw = String(error);
          errorInfo.errorValue = error;
          try {
            errorInfo.jsonString = JSON.stringify(error, null, 2);
          } catch {
            // skip
          }
          if (error === null || error === undefined) {
            errorInfo.nullOrUndefined = true;
          }
        }
        if (Object.keys(errorInfo).length <= 1 && (error === null || error === undefined)) {
          console.error('[Better Auth Client] Request error:', error === null ? 'null' : 'undefined');
        } else {
          console.error('[Better Auth Client] Request error:', errorInfo);
        }
      } else {
        let msg: string;
        if (error instanceof Error) {
          msg = error.message;
        } else if (error && typeof error === 'object') {
          const obj = error as Record<string, unknown>;
          if (typeof obj.message === 'string') {
            msg = obj.message;
          } else if (obj.response && typeof (obj.response as Response)?.status === 'number') {
            const res = obj.response as Response;
            msg = `HTTP ${res.status} ${(res.statusText != null && res.statusText !== '') ? res.statusText : ''}`.trim();
          } else {
            try {
              msg = JSON.stringify(error);
            } catch {
              msg = Object.prototype.toString.call(error);
            }
          }
        } else {
          msg = String(error);
        }
        console.error('[Better Auth Client] Request error:', msg);
      }
    },
  }
});

// Re-export commonly used hooks and functions
type AuthClientWithHooks = typeof authClient & {
  useSignIn: typeof authClient.signIn;
  useSignUp: typeof authClient.signUp;
  useSignOut: typeof authClient.signOut;
};

export const {
  useSession,
  signIn,
  signUp,
  signOut,
  useSignIn,
  useSignUp,
  useSignOut,
} = authClient as AuthClientWithHooks;

// Export types
export type Session = typeof authClient.$Infer.Session;
export type User = Session extends { user: infer U } ? U : never;

